const path = accountId => `/api/v1/accounts/${accountId}/ai_center`;
const client = () => window.axios;
const AUTH_HEADERS = ['access-token', 'token-type', 'client', 'expiry', 'uid'];

export default {
  // Com sort: 'urgency', a fila vem por urgência (Ai::CenterItemsFilter); o
  // status aceita vários, separados por vírgula.
  getItems(accountId, params = {}) {
    return client().get(`${path(accountId)}/items`, { params });
  },
  getItem(accountId, id) {
    return client().get(`${path(accountId)}/items/${id}`);
  },
  getSummary(accountId, params = {}) {
    return client().get(`${path(accountId)}/summary`, { params });
  },
  // Estado de cada área (ligada ou o que falta) e pendentes por aba.
  getOverview(accountId) {
    return client().get(`${path(accountId)}/overview`);
  },
  updateItem(accountId, id, payload) {
    return client().patch(`${path(accountId)}/items/${id}`, payload);
  },
  send(accountId, id, payload) {
    return client().post(`${path(accountId)}/items/${id}/send`, payload);
  },
  // force: o gestor assume a vez de quem está com o comentário.
  claim(accountId, id, { force = false } = {}) {
    return client().post(
      `${path(accountId)}/items/${id}/claim`,
      force ? { force: true } : undefined
    );
  },
  // Troca o imóvel do post por outro candidato; a sugestão é refeita em
  // segundo plano (o item volta em "analyzing").
  chooseProperty(accountId, id, { version, code }) {
    return client().post(`${path(accountId)}/items/${id}/property`, {
      version,
      code,
    });
  },
  release(accountId, id) {
    return client().post(`${path(accountId)}/items/${id}/release`);
  },
  // Pede a quem está com o comentário que solte a vez. O pedido aparece na
  // tela de quem está com ele no próximo ciclo, sem notificação no sino.
  askRelease(accountId, id) {
    return client().post(`${path(accountId)}/items/${id}/ask_release`);
  },
  // Dispensar e desfazer (comentário, retomada e imóvel). O desfazer só vale
  // nos minutos seguintes ao dispensar.
  dismiss(accountId, id, { version }) {
    return client().post(`${path(accountId)}/items/${id}/dismiss`, {
      version,
    });
  },
  restore(accountId, id, { version }) {
    return client().post(`${path(accountId)}/items/${id}/restore`, {
      version,
    });
  },
  // Solta a vez quando a aba ou o app fecha (pagehide). O axios não termina a
  // requisição com a página saindo; fetch com keepalive termina, e leva os
  // mesmos cabeçalhos de login do axios.
  releaseOnExit(accountId, id) {
    const defaults = client()?.defaults || {};
    const common = defaults.headers?.common || {};
    const headers = Object.fromEntries(
      AUTH_HEADERS.filter(key => common[key]).map(key => [key, common[key]])
    );
    const base = String(defaults.baseURL || '').replace(/\/$/, '');
    try {
      window
        .fetch(`${base}${path(accountId)}/items/${id}/release`, {
          method: 'POST',
          keepalive: true,
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', ...headers },
          body: '{}',
        })
        .catch(() => {});
    } catch {
      // A página já está saindo: a vez vence sozinha em 15 minutos.
    }
  },
  // Grupo de campanha (D7): quem comentou (status all, answered, failed,
  // waiting ou unanswered; 50 por página), marcar como visto e "Responder à
  // mão" numa pessoa, que devolve o item individual já criado (em analyzing
  // até a sugestão chegar).
  getCampaignMembers(accountId, id, params = {}) {
    return client().get(`${path(accountId)}/items/${id}/members`, { params });
  },
  markCampaignSeen(accountId, id) {
    return client().post(`${path(accountId)}/items/${id}/seen`);
  },
  promoteCampaignMember(accountId, id, memberId) {
    return client().post(
      `${path(accountId)}/items/${id}/members/${memberId}/promote`
    );
  },
  opportunityAction(accountId, id, action, payload) {
    return client().post(`${path(accountId)}/items/${id}/${action}`, payload);
  },
  // Envia o texto livre de uma retomada ou de um imóvel direto da Central,
  // com a janela de resposta aberta (Ai::OpportunityTextSend). A mesma
  // idempotency_key devolve o item já enviado, sem mandar de novo.
  sendText(accountId, id, { version, message, idempotencyKey }) {
    return client().post(`${path(accountId)}/items/${id}/send_text`, {
      version,
      message,
      idempotency_key: idempotencyKey,
    });
  },
  // Revisão da movimentação do Kanban (Ai::KanbanMoveReview): aprovar a
  // sugestão, desfazer a que já moveu (24 h), "Está certo" e reabrir a última
  // decisão (5 min). Dispensar usa o dismiss de sempre. Todas levam a versão
  // do item; 409 quer dizer que alguém mexeu antes.
  approveMove(accountId, id, { version }) {
    return client().post(`${path(accountId)}/items/${id}/approve_move`, {
      version,
    });
  },
  undoMove(accountId, id, { version }) {
    return client().post(`${path(accountId)}/items/${id}/undo_move`, {
      version,
    });
  },
  confirmMove(accountId, id, { version }) {
    return client().post(`${path(accountId)}/items/${id}/confirm_move`, {
      version,
    });
  },
  reopenMove(accountId, id, { version }) {
    return client().post(`${path(accountId)}/items/${id}/reopen_move`, {
      version,
    });
  },
  resend(accountId, id) {
    return client().post(`${path(accountId)}/items/${id}/resend`);
  },
  // Evento de conversão em sugestão (Ai::ConversionReview, só admin): Enviar
  // põe na fila de envio; Não enviar guarda quem escolheu, e o Desfazer dele
  // é o restore de sempre (7 dias). Levam a versão; 409 é versão velha.
  sendConversion(accountId, id, { version }) {
    return client().post(`${path(accountId)}/items/${id}/send_conversion`, {
      version,
    });
  },
  skipConversion(accountId, id, { version }) {
    return client().post(`${path(accountId)}/items/${id}/skip_conversion`, {
      version,
    });
  },
  getSettings(accountId) {
    return client().get(`${path(accountId)}/settings`);
  },
  // Só os tipos de comentário da conta (fila e item de Comentários), sem o
  // placar e as contagens que o GET de settings calcula para quem administra.
  getSocialCategories(accountId) {
    return client().get(`${path(accountId)}/settings/social_categories`);
  },
  // Só as situações do lead da conta (fila, item e modo rápido de Retomadas),
  // sem o placar que o GET de settings calcula para quem administra.
  getOpportunitySituations(accountId) {
    return client().get(`${path(accountId)}/settings/opportunity_situations`);
  },
  updateSettings(accountId, payload) {
    return client().patch(`${path(accountId)}/settings`, payload);
  },
  // Sincronização Notion para as FAQs do assistente (D39), só administrador.
  // O token do Notion fica no servidor e nunca vem nestas respostas.
  getKnowledgeSync(accountId) {
    return client().get(`${path(accountId)}/knowledge_sync`);
  },
  updateKnowledgeSync(accountId, payload) {
    return client().patch(`${path(accountId)}/knowledge_sync`, payload);
  },
  runKnowledgeSync(accountId) {
    return client().post(`${path(accountId)}/knowledge_sync/run`);
  },
  // Acervo da conta (D41): trechos de um banco do Notion para a fonte Acervo.
  getArchive(accountId) {
    return client().get(`${path(accountId)}/archive`);
  },
  updateArchive(accountId, payload) {
    return client().patch(`${path(accountId)}/archive`, payload);
  },
  runArchive(accountId) {
    return client().post(`${path(accountId)}/archive/run`);
  },
  getAgents(accountId, config = {}) {
    return client().get(`${path(accountId)}/agents`, config);
  },
  getAgent(accountId, kind, config = {}) {
    return client().get(`${path(accountId)}/agents/${kind}`, config);
  },
  updateAgent(accountId, kind, payload, config = {}) {
    return client().patch(`${path(accountId)}/agents/${kind}`, payload, config);
  },
  testAgent(accountId, kind, payload, config = {}) {
    return client().post(
      `${path(accountId)}/agents/${kind}/test`,
      payload,
      config
    );
  },
  getAgentTests(accountId, params = {}, config = {}) {
    return client().get(`${path(accountId)}/agent_tests`, {
      ...config,
      params,
    });
  },
  getAgentTest(accountId, id, config = {}) {
    return client().get(`${path(accountId)}/agent_tests/${id}`, config);
  },
};
